// Export de la liste des projets (spec § 2) : colonnes lisibles, source de chaque fiche, montant converti en euros
// au cours de la BCE (estimation). Mise en forme pure, testée ; le fichier est produit par ProjectExportButton.
import { projectName, projectStructureType } from "./projectText";
import type { Project } from "../types";
import { categoryKey, countryName, phaseKey } from "./projectUtils";

export type Cell = string | number | null;

type Options = {
  // Unités de la devise pour un euro (table `exchange_rates`).
  rates: Record<string, number>;
  translate: (key: string) => string;
  locale: string;
  // Adresse de l'interface, pour le lien vers chaque fiche.
  appUrl: string;
};

const day = (value?: string | null) => (value ? value.slice(0, 10) : null);

const amountEur = (project: Project, rates: Record<string, number>) => {
  const { budget_amount: amount, budget_currency: currency } = project;
  if (amount == null || !currency) return null;
  if (currency === "EUR") return Math.round(amount);
  const rate = rates[currency];
  return rate ? Math.round(amount / rate) : null;
};

const COLUMNS: [string, (project: Project, options: Options) => Cell][] = [
  ["score", (p) => p.score ?? null],
  ["name", (p, o) => projectName(p, o.locale)],
  ["category", (p, o) => o.translate(categoryKey(p.category))],
  ["structure_type", (p, o) => projectStructureType(p, o.locale)],
  ["phase", (p, o) => (p.phase ? o.translate(phaseKey(p.phase)) : null)],
  ["country", (p, o) => countryName(p.country, o.locale) || null],
  ["region", (p) => p.region ?? null],
  ["city", (p) => p.city ?? null],
  ["owners", (p) => p.owner_names ?? null],
  ["amount", (p) => p.budget_amount ?? null],
  ["currency", (p) => p.budget_currency ?? null],
  ["amount_eur", (p, o) => amountEur(p, o.rates)],
  ["funding", (p) => p.funding ?? null],
  ["start_date", (p) => p.start_date ?? null],
  ["end_date", (p) => p.end_date ?? null],
  ["source", (p) => p.source_name ?? null],
  ["source_url", (p) => p.source_url ?? null],
  ["published_at", (p) => day(p.source_published_at)],
  ["last_news_at", (p) => day(p.last_news_at)],
  ["detected_at", (p) => day(p.created_at)],
  ["aegyra_id", (p) => p.aegyra_project_id],
  ["link", (p, o) => `${o.appUrl}/#/projects/${p.id}/show`],
];

export const projectExportTable = (projects: Project[], options: Options) => ({
  headers: COLUMNS.map(([key]) =>
    options.translate(`resources.projects.export.${key}`),
  ),
  rows: projects.map((project) =>
    COLUMNS.map(([, value]) => value(project, options)),
  ),
});

// Une cellule qui commence par = + - @ serait lue comme une formule par un tableur : les textes viennent de
// sources publiques, on les neutralise par une apostrophe.
const FORMULA = /^[=+\-@\t\r]/;

export const textCell = (value: string) =>
  FORMULA.test(value) ? `'${value}` : value;

const csvCell = (value: Cell) => {
  if (value == null) return "";
  if (typeof value === "number") return String(value);
  const text = textCell(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/** CSV en UTF-8 avec marque d'ordre, pour qu'Excel lise les accents. */
export const toCsv = (headers: string[], rows: Cell[][]) =>
  "﻿" +
  [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n") +
  "\r\n";
